/**
 * Replenishment Settings Tab — Manage product-level replenishment parameters.
 * Editable table for min_stock, max_stock, safety_stock, reorder_point, lead_time, MOQ, order_multiple.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  TablePagination,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Close as CancelIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  FileUpload as FileUploadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import ExportMenu from '../Common/ExportMenu';
import * as XLSX from 'xlsx';
import UnifiedProductSearch, { ProductOption } from '../Common/UnifiedProductSearch';

interface ProductSettings {
  product_id: string;
  sku: string;
  product_name: string;
  brand: string;
  category: string;
  unit_cost: number | null;
  retail_price: number | null;
  min_stock: number | null;
  max_stock: number | null;
  safety_stock: number | null;
  reorder_point: number | null;
  lead_time_days: number | null;
  moq: number | null;
  order_multiple: number | null;
}

interface AnnouncedStockout {
  id?: string;
  product: ProductOption | null;
  start_date: Date | null;
  end_date: Date | null;
}

const PARAM_FIELDS = [
  { key: 'min_stock', label: 'Min Stock', tooltip: 'Minimum stock level before triggering alerts' },
  { key: 'max_stock', label: 'Max Stock', tooltip: 'Maximum stock capacity per store' },
  { key: 'safety_stock', label: 'Safety Stock', tooltip: 'Buffer stock to prevent stockouts during lead time variability' },
  { key: 'reorder_point', label: 'Reorder Point', tooltip: 'Stock level that triggers a replenishment order' },
  { key: 'lead_time_days', label: 'Lead Time (days)', tooltip: 'Days from order placement to delivery' },
  { key: 'moq', label: 'MOQ', tooltip: 'Minimum Order Quantity — smallest order the supplier accepts' },
  { key: 'order_multiple', label: 'Order Multiple', tooltip: 'Orders must be in multiples of this quantity (e.g., case size)' },
] as const;

const ReplenishmentSettingsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch product settings
  const { data: products, isLoading } = useQuery<ProductSettings[]>({
    queryKey: ['replenishmentSettings', brandFilter, categoryFilter, search],
    queryFn: async () => {
      const res = await api.getReplenishmentSettings({
        brand: brandFilter || undefined,
        category: categoryFilter || undefined,
        search: search || undefined,
      });
      return res.data;
    },
  });

  // Get unique brands and categories for filter dropdowns
  const brands = useMemo(() => {
    if (!products) return [];
    return [...new Set(products.map(p => p.brand).filter(Boolean))].sort();
  }, [products]);

  const categories = useMemo(() => {
    if (!products) return [];
    return [...new Set(products.map(p => p.category).filter(Boolean))].sort();
  }, [products]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async ({ productId, settings }: { productId: string; settings: any }) => {
      return api.updateReplenishmentSettings(productId, settings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['replenishmentSettings'] });
      setEditingRow(null);
      setEditValues({});
      setSnackbar({ open: true, message: 'Settings saved successfully', severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({ open: true, message: `Failed to save: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    },
  });

  const handleEdit = useCallback((product: ProductSettings) => {
    setEditingRow(product.product_id);
    setEditValues({
      min_stock: product.min_stock ?? 0,
      max_stock: product.max_stock ?? 0,
      safety_stock: product.safety_stock ?? 0,
      reorder_point: product.reorder_point ?? 0,
      lead_time_days: product.lead_time_days ?? 7,
      moq: product.moq ?? 1,
      order_multiple: product.order_multiple ?? 1,
    });
  }, []);

  const handleCancel = useCallback(() => {
    setEditingRow(null);
    setEditValues({});
  }, []);

  const handleSave = useCallback((productId: string) => {
    saveMutation.mutate({ productId, settings: editValues });
  }, [editValues, saveMutation]);

  const handleFieldChange = useCallback((field: string, value: string) => {
    const parsed = parseInt(value);
    if (!isNaN(parsed) && parsed >= 0) {
      setEditValues(prev => ({ ...prev, [field]: parsed }));
    }
  }, []);

  /* Upload state */
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  /* Announced stockouts */
  const [stockouts, setStockouts] = useState<AnnouncedStockout[]>([]);
  const [isSavingStockouts, setIsSavingStockouts] = useState(false);

  const { data: savedStockouts } = useQuery({
    queryKey: ['announced-stockouts'],
    queryFn: async () => {
      try {
        const response = await api.getSettings('announced_stockouts');
        return response.data;
      } catch {
        return null;
      }
    },
  });

  useEffect(() => {
    if (savedStockouts?.items) {
      setStockouts(
        savedStockouts.items.map((item: any) => ({
          id: item.id,
          product: item.product_id ? {
            product_id: item.product_id,
            product_name: item.product_name || '',
            sku: item.sku || '',
          } : null,
          start_date: item.start_date ? new Date(item.start_date) : null,
          end_date: item.end_date ? new Date(item.end_date) : null,
        })),
      );
    }
  }, [savedStockouts]);

  const handleAddStockout = () => {
    setStockouts((prev) => [...prev, { product: null, start_date: null, end_date: null }]);
  };

  const handleRemoveStockout = (index: number) => {
    setStockouts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStockoutProductChange = (index: number, products: ProductOption[]) => {
    setStockouts((prev) =>
      prev.map((s, i) => (i === index ? { ...s, product: products[0] || null } : s)),
    );
  };

  const handleSaveStockouts = async () => {
    setIsSavingStockouts(true);
    try {
      const stockoutItems = stockouts
        .filter((s) => s.product && s.start_date)
        .map((s) => ({
          product_id: s.product!.product_id,
          product_name: s.product!.product_name,
          sku: s.product!.sku,
          start_date: s.start_date!.toISOString(),
          end_date: s.end_date?.toISOString() || null,
        }));
      await api.saveSettings('announced_stockouts', { items: stockoutItems });
      queryClient.invalidateQueries({ queryKey: ['announced-stockouts'] });
      setSnackbar({ open: true, message: 'Announced stock-outs saved successfully', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Save failed: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setIsSavingStockouts(false);
    }
  };

  const REQUIRED_COLS = ['sku'] as const;
  const SETTING_KEYS = PARAM_FIELDS.map(f => f.key);

  /* Rows for <ExportMenu> — export ALL settings */
  const exportRows = useMemo(() => {
    if (!products?.length) return [];
    return products.map(p => ({
      SKU: p.sku,
      'Product Name': p.product_name,
      Brand: p.brand,
      Category: p.category,
      'Min Stock': p.min_stock ?? 0,
      'Max Stock': p.max_stock ?? 0,
      'Safety Stock': p.safety_stock ?? 0,
      'Reorder Point': p.reorder_point ?? 0,
      'Lead Time (days)': p.lead_time_days ?? 7,
      MOQ: p.moq ?? 1,
      'Order Multiple': p.order_multiple ?? 1,
    }));
  }, [products]);

  /* Upload Excel and bulk-update */
  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file || !products?.length) return;

    setUploading(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: Record<string, any>[] = XLSX.utils.sheet_to_json(ws);

      if (rows.length === 0) throw new Error('The file contains no data rows');

      // Normalize headers to lowercase for flexible matching
      const normalized = rows.map(r => {
        const out: Record<string, any> = {};
        Object.entries(r).forEach(([k, v]) => { out[k.toLowerCase().trim()] = v; });
        return out;
      });

      // Validate required column
      const first = normalized[0];
      if (!('sku' in first)) throw new Error('Missing required column: SKU');

      // Build SKU → product_id lookup
      const skuMap = new Map(products.map(p => [p.sku.toLowerCase(), p.product_id]));

      // Column header → setting field mapping
      const headerMap: Record<string, string> = {
        'min stock': 'min_stock', 'min_stock': 'min_stock',
        'max stock': 'max_stock', 'max_stock': 'max_stock',
        'safety stock': 'safety_stock', 'safety_stock': 'safety_stock',
        'reorder point': 'reorder_point', 'reorder_point': 'reorder_point',
        'lead time (days)': 'lead_time_days', 'lead time': 'lead_time_days', 'lead_time_days': 'lead_time_days',
        'moq': 'moq',
        'order multiple': 'order_multiple', 'order_multiple': 'order_multiple',
      };

      const updates: any[] = [];
      const errors: string[] = [];

      normalized.forEach((row, idx) => {
        const sku = String(row.sku || '').trim().toLowerCase();
        if (!sku) { errors.push(`Row ${idx + 2}: empty SKU`); return; }
        const pid = skuMap.get(sku);
        if (!pid) { errors.push(`Row ${idx + 2}: SKU "${row.sku}" not found`); return; }

        const settings: Record<string, number> = {};
        Object.entries(row).forEach(([k, v]) => {
          const field = headerMap[k];
          if (field && v !== '' && v !== null && v !== undefined) {
            const num = parseInt(String(v));
            if (!isNaN(num) && num >= 0) settings[field] = num;
          }
        });

        if (Object.keys(settings).length > 0) {
          updates.push({ product_id: pid, ...settings });
        }
      });

      if (updates.length === 0) throw new Error(errors.length > 0 ? errors.join('; ') : 'No valid settings found in file');

      const res = await api.bulkUpdateReplenishmentSettings(updates);
      const updated = res.data?.updated ?? updates.length;
      queryClient.invalidateQueries({ queryKey: ['replenishmentSettings'] });

      const msg = errors.length > 0
        ? `${updated} products updated. Warnings: ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? ` (+${errors.length - 3} more)` : ''}`
        : `${updated} products updated successfully`;
      setSnackbar({ open: true, message: msg, severity: errors.length > 0 ? 'warning' as any : 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Upload failed: ${err.message || err}`, severity: 'error' });
    } finally {
      setUploading(false);
    }
  }, [products, queryClient]);

  // Pagination
  const paginatedProducts = useMemo(() => {
    if (!products) return [];
    return products.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [products, page, rowsPerPage]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Box>
      {/* Filter Row */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search SKU or Product"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ endAdornment: <SearchIcon sx={{ color: 'var(--color-text-muted)' }} /> }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Brand</InputLabel>
              <Select value={brandFilter} label="Brand" onChange={(e) => { setBrandFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All Brands</MenuItem>
                {brands.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select value={categoryFilter} label="Category" onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              {products?.length || 0} products
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Info Banner + Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Alert severity="info" sx={{ flex: 1 }}>
          These parameters control replenishment order calculations. Click the edit icon on any row to modify values. Changes apply to all stores carrying the product.
        </Alert>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <ExportMenu
            data={exportRows}
            filename="Replenishment_Settings"
            title="Replenishment Settings"
            sheetName="Settings"
          />
          <Button
            variant="outlined" size="small"
            startIcon={uploading ? <CircularProgress size={14} /> : <FileUploadIcon sx={{ fontSize: 16 }} />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            sx={{ textTransform: 'none', fontWeight: 500, fontSize: '12px', whiteSpace: 'nowrap', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)', '&:hover': { borderColor: '#722F37', color: '#722F37' } }}
          >
            {uploading ? 'Uploading...' : 'Upload from Excel'}
          </Button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleUpload} />
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Brand</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Item Status</TableCell>
                  {PARAM_FIELDS.map((f) => (
                    <TableCell key={f.key} align="right" sx={{ fontWeight: 700, minWidth: 90 }}>
                      <Tooltip title={f.tooltip}>
                        <span style={{ cursor: 'help', borderBottom: '1px dotted var(--color-text-muted)' }}>{f.label}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 700, width: 80 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProducts.length > 0 ? paginatedProducts.map((product) => {
                  const isEditing = editingRow === product.product_id;
                  return (
                    <TableRow key={product.product_id} hover sx={{ bgcolor: isEditing ? 'rgba(114,47,55,0.04)' : undefined }}>
                      <TableCell>
                        <Typography sx={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                          {product.sku}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 'var(--text-sm)' }}>{product.product_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{product.brand}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={product.category} size="small" sx={{ fontSize: 'var(--text-xs)' }} />
                      </TableCell>
                      <TableCell sx={{ py: '6px', px: '8px' }}>
                        {(product as any).lifecycle_stage ? (
                          <Chip
                            label={(product as any).lifecycle_stage}
                            size="small"
                            color={
                              (product as any).lifecycle_stage === 'NEW' ? 'info' :
                              (product as any).lifecycle_stage === 'GROWTH' ? 'success' :
                              (product as any).lifecycle_stage === 'DECLINING' ? 'warning' :
                              ((product as any).lifecycle_stage === 'PHASE_OUT' || (product as any).lifecycle_stage === 'DISCONTINUED') ? 'error' :
                              'default'
                            }
                            variant="outlined"
                            sx={{ fontSize: '10px', height: 20 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                        )}
                      </TableCell>
                      {PARAM_FIELDS.map((f) => (
                        <TableCell key={f.key} align="right">
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={editValues[f.key] ?? ''}
                              onChange={(e) => handleFieldChange(f.key, e.target.value)}
                              inputProps={{ min: 0, style: { textAlign: 'right', width: 60, fontSize: 13, padding: '4px 8px' } }}
                              variant="outlined"
                            />
                          ) : (
                            <Typography sx={{
                              fontSize: 'var(--text-sm)',
                              fontVariantNumeric: 'tabular-nums',
                              color: (product as any)[f.key] == null ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
                              fontWeight: (product as any)[f.key] == null ? 400 : 500,
                            }}>
                              {(product as any)[f.key] ?? '-'}
                            </Typography>
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        {isEditing ? (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Save">
                              <IconButton size="small" color="success" onClick={() => handleSave(product.product_id)} disabled={saveMutation.isPending}>
                                {saveMutation.isPending ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={handleCancel}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Tooltip title="Edit settings">
                            <IconButton size="small" onClick={() => handleEdit(product)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={12 + PARAM_FIELDS.length} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: 'var(--color-text-muted)' }}>No products found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {(products?.length || 0) > 0 && (
            <TablePagination
              component="div"
              count={products?.length || 0}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          )}
        </Paper>
      )}

      {/* Announced Stock-outs */}
      <Paper sx={{ p: 3, mt: 3, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
            Announced Stock-outs
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined" size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleAddStockout}
            >
              Add Entry
            </Button>
            <Button
              variant="contained" color="success" size="small"
              startIcon={isSavingStockouts ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
              onClick={handleSaveStockouts}
              disabled={isSavingStockouts}
            >
              Save
            </Button>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '12px' }}>
          Declare known upcoming stock-outs so the system can adjust replenishment orders
          and exclude these periods from forecast accuracy calculations.
        </Typography>

        {stockouts.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '11px', width: '40%' }}>Product (SKU)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>End Date</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '11px', width: 50 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stockouts.map((stockout, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <UnifiedProductSearch
                        value={stockout.product ? [stockout.product] : []}
                        onChange={(products) => handleStockoutProductChange(idx, products)}
                        singleSelect
                        size="small"
                        placeholder="Search SKU..."
                      />
                    </TableCell>
                    <TableCell>
                      <DatePicker
                        value={stockout.start_date}
                        onChange={(d) => setStockouts((prev) => prev.map((s, i) => i === idx ? { ...s, start_date: d } : s))}
                        slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }}
                      />
                    </TableCell>
                    <TableCell>
                      <DatePicker
                        value={stockout.end_date}
                        onChange={(d) => setStockouts((prev) => prev.map((s, i) => i === idx ? { ...s, end_date: d } : s))}
                        slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleRemoveStockout(idx)}>
                        <DeleteIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 3, fontSize: '13px' }}>
            No announced stock-outs. Click "Add Entry" to declare one.
          </Typography>
        )}
      </Paper>

      {/* Snackbar for save feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </LocalizationProvider>
  );
};

export default ReplenishmentSettingsTab;
